// src/helpers/eirna_backend_helper.js
import { APIClient } from "./api_helper";
import * as url from "./url_helper";

// Create separate API clients for public and authenticated requests
const publicApi = new APIClient(false); // No auth for login
const authApi = new APIClient(true); // Auth required for other calls

// Authentication - No token needed
export const postLogin = (data) => publicApi.create(url.POST_LOGIN, data);
export const postRegister = (data) => publicApi.create(url.POST_REGISTER, data);
export const postForgotPassword = (data) =>
  publicApi.create(url.POST_FORGOT_PASSWORD, data);
export const postResetPassword = (token, data) =>
  publicApi.create(url.POST_RESET_PASSWORD(token), data);

// Landing page - Token required
export const getLandingData = () => authApi.get(url.GET_PROJECTS);

export const sendUserInvite = (data) =>
  authApi.create(url.POST_SEND_INVITE, data);

export const sendContactMessage = (data) =>
  authApi.create(url.POST_CONTACT_US, data);

export const getUpdates = () => authApi.get(url.GET_UPDATES);

export const getSettings = () => authApi.get(url.GET_SETTINGS);

export const updateSettings = (data) =>
  authApi.create(url.POST_SETTINGS_UPDATE, data);

export const getPlusStatus = (projectId) =>
   authApi.get(url.PLUS_STATUS(projectId));

export const getSampleGroupingsData = (projectId) =>
   authApi.get(url.SAMPLE_GROUPING(projectId));

export const postSampleGroupingsData = (data) =>
   authApi.create(url.POST_SAMPLE_GROUPING, data);

export const postCreateBatchCode = (data) =>
   authApi.create(url.POST_CREATE_BATCH_CODE, data);

export const postCreateBatchDownload = (data) =>
   authApi.create(url.POST_CREATE_BATCH_DOWNLOAD, data);

export const getInteractivePlotData = (projectId) =>
  authApi.get(url.GET_INTERACTIVE_PLOT_DATA(projectId));

export const getBatchProfileData = (projectId) =>
  authApi.get(url.GET_BATCH_PROFILE_DATA(projectId));

export const generateBatchSubcodonPlot = (data) =>
  authApi.create(url.POST_GENERATE_BATCH_SUBCODON_PLOT, data);

export const generateSubcodonPlot = (data) =>
  authApi.create(url.POST_GENERATE_SUBCODON_PLOT, data);

export const getComparisonPlotData = (projectId) =>
  authApi.get(url.GET_COMPARISON_PLOT_DATA(projectId));

export const getBatchComparsionProfileData = (projectId) =>
  authApi.get(url.GET_BATCH_COMPARISON_PROFILE_DATA(projectId));

export const generateComparisonPlot = (data) =>
  authApi.create(url.POST_GENERATE_COMPARISON_PLOT, data);

export const generateBatchComparisonPlot = (data) =>
  authApi.create(url.POST_BULK_COMPARE_QUERY, data);

// Data Quality Plot API functions
export const getDataQualityPlotData = (projectId) =>
  authApi.get(url.GET_DATA_QUALITY_PLOT_DATA(projectId));

export const generateDataQualityPlot = (data) =>
  authApi.create(url.POST_GENERATE_DATA_QUALITY_PLOT, data);

// Differential Expression API functions
export const getDifferentialPlotData = (projectId) =>
  authApi.get(url.GET_DIFFERENTIAL_PLOT_DATA(projectId));

export const generateDifferentialExpressionPlot = (data) =>
  authApi.create(url.POST_GENERATE_DIFFERENTIAL_PLOT, data);

export const fetchPauseDetectionData = (projectId) =>
  authApi.get(url.GET_PAUSE_DETECTION_DATA(projectId));

export const submitPauseDetectionJob = (data) =>
  authApi.create(url.POST_GENERATE_PAUSE_DETECTION, data);

export const fetchJobStatus = (taskId) =>
  authApi.get(url.GET_TASK_STATUS(taskId));

export const fetchTranslonExplorerData = (projectId) =>
  authApi.get(url.GET_TRANSLON_EXPLORER_DATA(projectId));

export const submitTranslonExplorerJob = (data) =>
  authApi.create(url.POST_GENERATE_TRANSLON_EXPLORER, data);

export const fetchCodonOccupancyData = (projectId) =>
  authApi.get(url.GET_CODON_OCCUPANCY_DATA(projectId));

export const submitCodonOccupancyJob = (data) =>
  authApi.create(url.POST_GENERATE_CODON_OCCUPANCY, data);

export const fetchTranscriptomeInfoData = (projectId) =>
  authApi.get(url.GET_TRANSCRIPTOME_INFO_DATA(projectId));

export const submitTranscriptomeInfoJob = (data) =>
  authApi.create(url.POST_GENERATE_TRANSCRIPTOME_INFO, data);

export const getTimeSeriesData = (projectId) =>
  authApi.get(url.GET_TIME_SERIES_DATA(projectId));

export const generateTimeSeriesPlot = (data) =>
  authApi.create(url.POST_GENERATE_TIME_SERIES_PLOT, data);

export const getPolyShiftData = (projectId) =>
  authApi.get(url.GET_POLYSHIFT_DATA(projectId));

export const generatePolyShiftPlot = (data) =>
  authApi.create(url.POST_GENERATE_POLYSHIFT_PLOT, data);

export const getShortCodeData = (shortCode) =>
  authApi.get(url.GET_SHORT_CODE_DATA(shortCode));

export const getDifferentialSilhouetteData = (projectId) =>
  authApi.get(url.GET_DIFFERENTIAL_SILHOUETTE_DATA(projectId));

export const generateDifferentialSilhouettePlot = (data) =>
  authApi.create(url.POST_DIFFERENTIAL_SILHOUETTE_QUERY, data);

export const getDifferentialCdsLeaderData = (projectId) =>
  authApi.get(url.GET_DIFFERENTIAL_CDS_LEADER_DATA(projectId));

export const generateDifferentialCdsLeaderPlot = (data) =>
  authApi.create(url.POST_DIFFERENTIAL_CDS_LEADER_QUERY, data);

export const getRRTSData = (projectId) =>
  authApi.get(url.GET_RRTS_DATA(projectId));

export const generateRRTSTable = (data) =>
  authApi.create(url.POST_GENERATE_RRTS_TABLE, data);

export const getUTRPeriodicityData = (projectId) =>
  authApi.get(url.GET_UTR_PERIODICITY_DATA(projectId));

export const generateUTRPeriodicityPlot = (data) =>
  authApi.create(url.POST_UTR_PERIODICITY_QUERY, data);

export const getTerminationMetageneData = (projectId) =>
  authApi.get(url.GET_TERMINATION_METAGENE_DATA(projectId));

export const generateTerminationMetagenePlot = (data) =>
  authApi.create(url.POST_TERMINATION_METAGENE_QUERY, data);

export const getSingleGenomeBrowserData = (projectId, html_params) =>
  authApi.get(url.GET_SINGLE_GENOME_BROWSER_DATA(projectId, html_params));

export const getComparisonGenomeBrowserData = (projectId, html_params) =>
  authApi.get(url.GET_COMPARISON_GENOME_BROWSER_DATA(projectId, html_params));
